import { useState } from 'react';
import { useForm } from '@moda/portal-stanchions';
import { CONFIG } from '../../config';
import { tc } from '../../lib/trackingContext';

type ModaPrivateFormFields = {
  name: string;
  email: string;
  phone: string;
  channel: string;
  location: string;
  message: string;
  area: string;
  investment: string;
  terms: boolean;
  size: string;
  subscribe: boolean;
};

const VALIDATIONS = {
  name: {
    required: {
      message: 'Please enter a name.',
      value: true
    }
  },
  email: {
    email: {
      message: 'Invalid email address.',
      value: true
    },
    required: {
      message: 'Please enter an email address.',
      value: true
    }
  },
  phone: {
    phoneNumber: {
      message: 'Invalid phone number.',
      value: true
    },
    required: {
      message: 'Please enter a phone number.',
      value: true
    }
  },
  location: {
    required: {
      message: 'Please enter your location.',
      value: true
    }
  }
};

const submitRequest = ({
  name,
  email,
  phone,
  channel,
  location,
  message,
  area,
  investment,
  size,
  subscribe
}: ModaPrivateFormFields): Promise<{ success: boolean }> =>
  fetch(`${CONFIG.MODA_API_URL}/v3.5/moda_private/request_consultation`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name,
      email,
      phone,
      preferred_channel: channel,
      priority_area: area,
      investment,
      location,
      message,
      size,
      subscribe
    })
  }).then(response => (response.ok ? response.json() : { success: false }));

export const useModaPrivateForm = ({ onSuccess }: { onSuccess: () => void }) => {
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (values: ModaPrivateFormFields) => {
    if (!values.terms) {
      setError(new Error('Please accept the Terms & Conditions.'));
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const { success } = await submitRequest(values);
      if (!success) throw new Error();
      if (success) {
        tc.track('Moda Private Form Submitted', {
          addToTrackingEvent: {
            name: values.name,
            email: values.email,
            phone: values.phone,
            channel: values.channel,
            location: values.location,
            message: values.message,
            size: values.size,
            subscribe: values.subscribe,
            terms: values.terms,
            label: 'Submit',
            form_location: 'Moda Private Form'
          }
        });
      }
      onSuccess();
    } catch {
      setError(new Error('Unable to send your request at this time.'));
    }

    setLoading(false);
  };

  const { errors: formErrors, handleSubmit } = useForm({ validations: VALIDATIONS, onSubmit });

  return { handleSubmit, formErrors, error, loading };
};
